import type { Theme as ExtendedTheme } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import React from 'react';

import {
    Article,
    ArticleSummary,
    Channel,
    IdAndName,
    ListItem,
    Publisher,
} from '../../shared/types';
import { formatDisplayDateTime } from '../../utils/date.utils';
import { getPublisherUrl } from '../../utils/utils';
import { getChannelLink } from '../../utils/link.utils';
import {
    getPrintLinkColor,
    getPrintTextSecondaryColor,
} from '../../utils/print-theme.utils';

import Link from 'components/ui/AppLink';

type StyleName = 'caption1' | 'caption2';

export interface PublishedByProps {
    date: Date;
    publisher: ListItem;
    className?: string;
    styleName?: StyleName;
    momentInTimeFormat: boolean;
    showEditDate: boolean;
    editDate?: Date;
    simplified?: boolean;
    channel?: Channel;
    disabledPublishedDate?: boolean;
}

const publishedByRootSx = (theme: ExtendedTheme) => {
    const printTextColor = getPrintTextSecondaryColor(theme);
    const printLinkColor = getPrintLinkColor(theme);

    return {
        '@media print': {
            color: printTextColor,
            '& a, & b, & strong': {
                color: printLinkColor,
            },
            '& span': {
                color: printTextColor,
            },
        },
        [theme.breakpoints.down('xs')]: {
            '& a': {
                fontSize: 10,
            },
        },
        [theme.breakpoints.up('md')]: {
            mb: 2,
        },
        [theme.breakpoints.up('xl')]: {
            '& a': {
                fontSize: 12,
            },
        },
    };
};

const publishedByLinkSx = (styleName: StyleName) => (theme: ExtendedTheme) => ({
    ...(styleName === 'caption1'
        ? (theme.typography.caption as any)
        : styleName === 'caption2'
          ? (theme.typography.overline as any)
          : {}),
    fontWeight: 'fontWeightBold',
});

const PublishedBy = (props: PublishedByProps): JSX.Element => {
    const {
        styleName,
        date,
        momentInTimeFormat,
        showEditDate,
        editDate,
        publisher,
        simplified,
        channel,
        disabledPublishedDate,
    } = props;
    const formattedStyleName = styleName ?? 'caption2';
    const dateString = formatDisplayDateTime(date, momentInTimeFormat);

    const channelData = channel ? (
        <>
            {' '}
            in{' '}
            <Link
                to={getChannelLink(channel._id)}
                sx={publishedByLinkSx(formattedStyleName)}
            >
                <strong>{channel.name}</strong>
            </Link>
        </>
    ) : (
        <></>
    );

    const editDisplay = disabledPublishedDate && showEditDate && editDate && (
        <span>
            <Box
                component="span"
                sx={{
                    display: 'inline-block',
                    height: 4,
                    width: 4,
                    backgroundColor: 'divider',
                    ml: 1,
                    mr: 1,
                    mb: 0.25,
                }}
            />
            Last edited {formatDisplayDateTime(editDate, momentInTimeFormat)}
        </span>
    );

    if (simplified) {
        return (
            <Typography
                variant="caption"
                sx={{
                    color: 'text.secondary',
                }}
            >
                {dateString} <> by </>
                <Link id="published-by-link" to={publisher.url}>
                    <b>{publisher.name}</b>
                </Link>
            </Typography>
        );
    }

    return (
        <Typography
            component="span"
            variant={formattedStyleName === 'caption1' ? 'caption' : 'body2'}
            className={props.className}
            sx={[{ color: 'text.secondary' }, publishedByRootSx]}
        >
            Published {disabledPublishedDate ? dateString : ''}
            {channelData}by{' '}
            <Link
                id="published-by-link"
                sx={publishedByLinkSx(formattedStyleName)}
                to={publisher.url}
            >
                {publisher.name}
            </Link>
            {editDisplay}
        </Typography>
    );
};

export default PublishedBy;

export function getPublishedData(
    article: ArticleSummary | Article,
    sectionName: string,
    momentInTimeFormat: boolean,
    showEditDate: boolean
): PublishedByProps {
    return getPublishedDataByFields(
        article.publishDate,
        article.publishedBy,
        article.categories,
        sectionName,
        momentInTimeFormat,
        showEditDate,
        article.lastEdited
    );
}

function getPublishedDataByFields(
    publishDate: Date | undefined,
    publishedBy: Publisher,
    categories: IdAndName[],
    sectionName: string,
    momentInTimeFormat: boolean,
    showEditDate: boolean,
    editDate: Date | undefined
): PublishedByProps {
    const dt = publishDate ? new Date(publishDate) : new Date();
    return {
        date: dt,
        publisher: entityToListItem(
            publishedBy._id,
            publishedBy.name,
            getPublisherUrl(publishedBy)
        ),
        momentInTimeFormat: momentInTimeFormat,
        showEditDate: showEditDate,
        editDate: editDate,
    };
}

function entityToListItem(
    id: string,
    name: string,
    urlPrefix: string
): ListItem {
    return {
        id,
        name,
        url: urlPrefix,
    };
}
